import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export type AwsConnectionStatus = 'PENDING_VERIFICATION' | 'ACTIVE' | 'FAILED';

/**
 * A single connected AWS account. One subscription's AWS integration can expose
 * one or many accounts (today the connection yields one; the `accounts` array on
 * the connection lets the backend surface several without any UI change).
 */
export interface AwsAccount {
  accountId:      string;
  label?:         string;   // optional friendly name; falls back to the account id
  defaultRegion:  string;
  enabledRegions: string[];
  /** True for the subscription's primary account (anchor-backed). */
  isPrimary?:        boolean;
  connectionStatus?: AwsConnectionStatus;
  /** Coarse health derived by the backend (OK / STALE / ERROR / UNKNOWN). */
  healthStatus?:     string;
}

export interface AwsConnection {
  /** Stable UUID generated by KernalHQ — never changes once created. */
  externalId:        string;
  connectionStatus:  AwsConnectionStatus;
  /** KernalHQ's own AWS account ID — shown in the trust policy setup guide. */
  kernalHqAccountId: string;
  roleArn?:          string;
  /** The default region for the primary account connection. */
  defaultRegion?:    string;
  /** All regions enabled for this account connection. */
  enabledRegions?:   string[];
  lastTestedAt?:      string;
  failureReason?:     string;
  connectedAt?:       string;
  updatedAt?:         string;
  /** Connected AWS account ID — populated after a successful testConnection. */
  accountId?:         string;
  /** Full caller identity ARN of the assumed role — populated after testConnection. */
  callerIdentityArn?: string;
  /**
   * All first-class AWS accounts reachable through this subscription's AWS
   * integration. Drives the Cloud-page account selector.
   */
  accounts?:          AwsAccount[];
  /** The subscription's primary (anchor-pointed) account id. */
  primaryAccountId?:  string;
  /** Number of connected accounts (= accounts.length). */
  accountCount?:      number;
}

export interface AwsTestResult {
  success:           boolean;
  message:           string;
  connectionStatus:  AwsConnectionStatus;
  accountId?:        string;
  callerIdentityArn?: string;
  failureCode?:      string;
  /** True when the verified account became/refreshed the subscription's primary account. */
  isPrimary?:        boolean;
  /** True when this account was already connected (idempotent re-verify, not a new add). */
  alreadyConnected?: boolean;
}

/** One AWS account as returned by GET /aws/accounts (secrets stripped). */
export interface AwsAccountListResponse {
  accounts:         AwsAccount[];
  primaryAccountId?: string;
  accountCount:     number;
}

@Injectable({ providedIn: 'root' })
export class AwsService {
  private readonly base = `${environment.apiUrl}/api/v1/integrations/aws`;

  constructor(private readonly http: HttpClient) {}

  /**
   * Returns the current user's AWS connection record.
   * Always succeeds — if no record exists, the backend creates one with a
   * stable externalId so the customer can configure their trust policy.
   * Returns null only on an unexpected HTTP error.
   */
  getConnection(): Observable<AwsConnection | null> {
    return this.http.get<AwsConnection>(`${this.base}/connection`).pipe(
      catchError(err => {
        if (err.status === 404) return of(null);
        throw err;
      }),
    );
  }

  /**
   * Tests (and connects) an AWS cross-account role. The backend assumes the role
   * using the stored externalId, calls GetCallerIdentity, and idempotently
   * upserts a per-account record keyed by the verified account id — so this is
   * also the "Add AWS account" endpoint. Adding a new account never overwrites
   * an existing one. Never exposes temporary credentials.
   */
  testConnection(roleArn: string, region: string, displayName?: string): Observable<AwsTestResult> {
    const body: { roleArn: string; region: string; displayName?: string } = { roleArn, region };
    if (displayName) body.displayName = displayName;
    return this.http.post<AwsTestResult>(`${this.base}/test-connection`, body);
  }

  /** Lists every AWS account connected under this subscription. */
  listAccounts(): Observable<AwsAccountListResponse> {
    return this.http.get<AwsAccountListResponse>(`${this.base}/accounts`);
  }

  /** Disconnects a single AWS account without affecting the others. */
  deleteAccount(accountId: string): Observable<{ message: string; accountId: string }> {
    return this.http.delete<{ message: string; accountId: string }>(`${this.base}/accounts/${accountId}`);
  }

  /**
   * Updates the enabled regions and default region for the AWS account
   * connection. The backend validates that the default is one of the enabled
   * regions and that at least one region is enabled.
   */
  updateRegions(defaultRegion: string, enabledRegions: string[]): Observable<{ defaultRegion: string; enabledRegions: string[] }> {
    return this.http.patch<{ defaultRegion: string; enabledRegions: string[] }>(`${this.base}/regions`, { defaultRegion, enabledRegions });
  }

  /**
   * Updates the enabled regions and default region for ONE specific AWS account,
   * leaving the other accounts untouched. The backend validates default ∈ enabled
   * and at least one enabled region.
   */
  updateAccountRegions(accountId: string, defaultRegion: string, enabledRegions: string[]): Observable<{ accountId: string; defaultRegion: string; enabledRegions: string[] }> {
    return this.http.patch<{ accountId: string; defaultRegion: string; enabledRegions: string[] }>(
      `${this.base}/accounts/${accountId}/regions`,
      { defaultRegion, enabledRegions },
    );
  }

  /**
   * Removes the AWS connection record for the current user.
   */
  disconnect(): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.base}/connection`);
  }
}
